import { useDispatch } from "react-redux";

import { Map } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import { COLOR_SCALE_MAP, COLORS } from "@/constants/colors";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { updateDatasetStyle } from "@/store/slice/datasetSlice";
import { OrdinalRange } from "@/types/common";
import { Dataset, DatasetAttributeSchema, DatasetStyle } from "@/types/dataset";
import { generateColorRanges, isValidPointsData } from "@/utils/common";
import {
  calculateFeaturePropertyColorRanges,
  isOrdinalType
} from "@/utils/dataset";
import {
  addClusterOrdinalLayers,
  addClusterQuantileLayers,
  addClusterSource
} from "@/utils/map/point";

type UsePointLayersProps = {
  map: Map;
  handleFeatureClick: (layerId: string) => void;
  handleMovePositionToLayer: (polygonData: FeatureCollection<Geometry>) => void;
  handleUpdateSourceWithRank: (value: {
    datasetId: string;
    style: DatasetStyle;
    update: {
      isEnabled?: boolean;
      maxRanking?: number;
      category?: DatasetAttributeSchema;
    };
  }) => Promise<void>;
};

export const usePointLayers = ({
  map,
  handleFeatureClick,
  handleMovePositionToLayer,
  handleUpdateSourceWithRank
}: UsePointLayersProps) => {
  const dispatch = useDispatch();
  const { isNewUIDatasetConfigEnabled, isRankingLabelEnabled } =
    useAppFeatures();

  const handleAddPointsLayer = (
    dataset: Dataset,
    pointsData: FeatureCollection<Geometry>,
    customId?: string,
    filteredData?: FeatureCollection<Geometry>,
    beforeId?: string
  ) => {
    const sourceId = customId || dataset.data.id;
    if (!isValidPointsData(map, pointsData) || !map) {
      return;
    }
    if (map.getSource(sourceId)) return;

    const newLayer = {
      id: sourceId,
      type: dataset.properties.spatialType,
      sublayers: {
        circle: `${dataset.data.id}-circle`,
        count: `${dataset.data.id}-count`,
        unclustered: `${dataset.data.id}-unclustered-points`,
        label: `${dataset.data.id}-label`,
        rank: `${dataset.data.id}-rank`
      }
    };

    if (filteredData) {
      addClusterSource(map, sourceId, filteredData); // filtered data
    } else {
      addClusterSource(map, sourceId, pointsData);
    }

    let updatedStyle: DatasetStyle;

    const isOrdinal = isOrdinalType(dataset.selectedCategory.data_type);
    const colorSteps = dataset.style.colorSteps || 10;
    const colorFamily = dataset.style.colorFamily || "TEAL";

    const ranges = calculateFeaturePropertyColorRanges(
      pointsData, // original data
      dataset.selectedCategory.attribute_name,
      isOrdinal,
      dataset.style.colorSteps || 10
    );

    if (isOrdinal) {
      // Ordinal color scale
      const ordinalRanges = ranges as OrdinalRange[];
      const colorRanges = ordinalRanges.map((range) => {
        if (!range.category) {
          return {
            value: "",
            color: "#D3D3D3",
            label: "null"
          };
        }
        return {
          value: range.category,
          color: COLORS[colorFamily][COLOR_SCALE_MAP[colorSteps][range.color]],
          label: range.category
        };
      });

      updatedStyle = {
        ...(dataset.style || {}),
        colorRanges,
        colorFamily,
        rankConfig: {
          isEnabled: dataset.style?.rankConfig?.isEnabled ?? false,
          maxRanking: Math.min(
            dataset.style?.rankConfig?.maxRanking ?? 1,
            (filteredData ?? pointsData).features.length
          )
        }
      };

      addClusterOrdinalLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle,
        dataset.selectedCategory.attribute_name,
        beforeId
      );
    } else {
      // Quantile scale
      const colorRanges = generateColorRanges(
        ranges,
        colorFamily,
        dataset.selectedCategory.data_type,
        colorSteps
      );

      updatedStyle = {
        ...(dataset.style || {}),
        colorRanges,
        colorFamily,
        rankConfig: {
          isEnabled: dataset.style?.rankConfig?.isEnabled ?? false,
          maxRanking: Math.min(
            dataset.style?.rankConfig?.maxRanking ?? 1,
            (filteredData ?? pointsData).features.length
          )
        }
      };

      addClusterQuantileLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle,
        dataset.selectedCategory.attribute_name,
        beforeId
      );
    }

    handleUpdateSourceWithRank({
      datasetId: sourceId,
      style: updatedStyle,
      update: {}
    });

    dispatch(
      updateDatasetStyle({ datasetId: dataset.data.id, style: updatedStyle })
    );

    handleFeatureClick(newLayer.sublayers.circle);
    handleFeatureClick(newLayer.sublayers.unclustered);

    if (dataset.properties.isVisible) handleMovePositionToLayer(pointsData);
  };

  return {
    handleAddPointsLayer
  };
};

export default usePointLayers;
